import { useState, useEffect, useCallback } from 'react';
import { Table, Button, Modal, Form, Input, Tag, message, Tabs, Popconfirm, Switch, InputNumber, Space, Tooltip } from 'antd';
import { SearchOutlined, ReloadOutlined, CopyOutlined, FileTextOutlined } from '@ant-design/icons';
import { adminAPI } from '../services/api';



const Affiliates = () => {
  const [affiliates, setAffiliates] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [affiliateModalVisible, setAffiliateModalVisible] = useState(false);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [selectedWithdrawal, setSelectedWithdrawal] = useState(null);
  const [selectedAffiliate, setSelectedAffiliate] = useState(null);
  const [affiliateForm] = Form.useForm();
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
  const [searchCode, setSearchCode] = useState('');
  const [searchEmail, setSearchEmail] = useState('');
  const [sortBy, setSortBy] = useState('');
  const [sortOrder, setSortOrder] = useState('');

  // 复制到剪贴板
  const handleCopy = (text, label) => {
    // 使用textarea作为fallback，解决HTTP环境下navigator.clipboard不可用的问题
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    try {
      document.execCommand('copy');
      message.success(`${label}已复制`);
    } catch (err) {
      message.error('复制失败');
    }
    document.body.removeChild(textarea);
  };

  // 查看详情
  const handleViewDetail = (record) => {
    setSelectedWithdrawal(record);
    setDetailModalVisible(true);
  };

  // 获取代理商列表
  const fetchAffiliates = useCallback(async (page, pageSize, code, email, sortField, sortOrd) => {
    setLoading(true);
    try {
      const params = { page, page_size: pageSize };
      if (code) params.code = code;
      if (email) params.email = email;
      if (sortField) { params.sort_by = sortField; params.sort_order = sortOrd; }

      const response = await adminAPI.getAffiliates(params);
      if (response.code === 0) {
        setAffiliates(response.data.list || []);
        setPagination(prev => ({ ...prev, current: page, pageSize, total: response.data.total || 0 }));
      }
    } catch (error) {
      message.error('获取代理商列表失败');
    } finally {
      setLoading(false);
    }
  }, []);

  // 获取提现列表
  const fetchWithdrawals = async (status = '') => {
    try {
      const response = await adminAPI.getWithdrawals(status);
      if (response.code === 0) setWithdrawals(response.data.list || []);
    } catch (error) {
      console.error('获取提现列表失败');
    }
  };

  useEffect(() => {
    fetchAffiliates(1, 20, '', '', '', '');
    fetchWithdrawals();
  }, []);

  const handleSearch = () => fetchAffiliates(1, pagination.pageSize, searchCode, searchEmail, sortBy, sortOrder);
  const handleReset = () => { setSearchCode(''); setSearchEmail(''); setSortBy(''); setSortOrder(''); fetchAffiliates(1, pagination.pageSize, '', '', '', ''); };

  const handleTableChange = (newPagination, filters, sorter) => {
    const newSortBy = sorter.field || '';
    const newSortOrder = sorter.order === 'ascend' ? 'asc' : sorter.order === 'descend' ? 'desc' : '';
    setSortBy(newSortBy);
    setSortOrder(newSortOrder);
    fetchAffiliates(newPagination.current, newPagination.pageSize, searchCode, searchEmail, newSortBy, newSortOrder);
  };

  const handleToggleStatus = async (record) => {
    const newStatus = record.status === 'active' ? 'suspended' : 'active';
    try {
      const response = await adminAPI.updateAffiliateStatus(record.id, newStatus);
      if (response.code === 0) { message.success(newStatus === 'active' ? '已启用' : '已禁用'); fetchAffiliates(pagination.current, pagination.pageSize, searchCode, searchEmail, sortBy, sortOrder); }
      else message.error(response.message || '操作失败');
    } catch (error) { message.error('操作失败'); }
  };

  const handleEditAffiliate = (record) => { setSelectedAffiliate(record); affiliateForm.setFieldsValue({ commission_rate: record.commission_rate }); setAffiliateModalVisible(true); };
  const handleAffiliateSubmit = async (values) => {
    try {
      const response = await adminAPI.updateAffiliate(selectedAffiliate.id, values);
      if (response.code === 0) { message.success('设置成功'); setAffiliateModalVisible(false); fetchAffiliates(pagination.current, pagination.pageSize, searchCode, searchEmail, sortBy, sortOrder); }
      else message.error(response.message || '设置失败');
    } catch (error) { message.error('设置失败'); }
  };

  const handleProcessWithdrawal = async (id, action, rejectReason = '') => {
    try {
      const response = await adminAPI.processWithdrawal(id, { action, reject_reason: rejectReason });
      if (response.code === 0) { message.success('处理成功'); fetchWithdrawals(); }
      else message.error(response.message || '处理失败');
    } catch (error) { message.error('处理失败'); }
  };

  const affiliateColumns = [
    { title: 'ID', dataIndex: 'id', width: 60 },
    { title: '推广码', dataIndex: 'code', width: 120, render: (code) => <Tag color="blue">{code}</Tag> },
    { title: '用户邮箱', dataIndex: ['user', 'email'], width: 200 },
    { title: '佣金比例', dataIndex: 'commission_rate', width: 100, sorter: true, render: (rate) => `${(rate * 100).toFixed(1)}%` },
    { title: '累计佣金', dataIndex: 'total_earnings', width: 110, sorter: true, render: (v) => `¥${((v || 0) / 100000).toFixed(2)}` }, // 厘 -> 元
    { title: '待提现', dataIndex: 'pending_balance', width: 100, sorter: true, render: (v) => `¥${((v || 0) / 100000).toFixed(2)}` }, // 厘 -> 元
    { title: '状态', dataIndex: 'status', width: 80, render: (status, record) => <Switch checked={status === 'active'} onChange={() => handleToggleStatus(record)} checkedChildren="启用" unCheckedChildren="禁用" size="small" /> },
    { title: '注册时间', dataIndex: 'created_at', width: 150, sorter: true, render: (time) => time ? new Date(time).toLocaleString() : '-' },
    { title: '操作', width: 70, fixed: 'right', render: (_, record) => <Button type="link" size="small" onClick={() => handleEditAffiliate(record)}>设置</Button> },
  ];

  const withdrawalColumns = [
    { title: 'ID', dataIndex: 'id', width: 60 },
    { 
      title: '代理商', 
      dataIndex: 'affiliate_email',
      width: 180,
      render: (v, record) => v || record.affiliate_name || record.affiliate_code || '-'
    },
    { title: '金额', dataIndex: 'amount', width: 90, render: (v) => <span style={{ color: '#52c41a', fontWeight: 'bold' }}>¥{parseFloat(v).toFixed(2)}</span> },
    { 
      title: '银行卡号', 
      dataIndex: 'account_number', 
      width: 140,
      render: (v) => v ? (
        <Space size={4}>
          <span>{v.replace(/(\d{4})\d+(\d{4})/, '$1****$2')}</span>
          <Tooltip title="复制卡号"><CopyOutlined style={{ cursor: 'pointer', color: '#1890ff' }} onClick={() => handleCopy(v, '银行卡号')} /></Tooltip>
        </Space>
      ) : '-'
    },
    { title: '持卡人', dataIndex: 'account_name', width: 90, ellipsis: true },
    { title: '银行', dataIndex: 'bank_name', width: 100, ellipsis: true },
    { title: '状态', dataIndex: 'status', width: 80, render: (status) => { const colors = { pending: 'orange', approved: 'blue', rejected: 'red', paid: 'green' }; const labels = { pending: '待处理', approved: '已批准', rejected: '已拒绝', paid: '已支付' }; return <Tag color={colors[status]}>{labels[status]}</Tag>; } },
    { title: '申请时间', dataIndex: 'created_at', width: 150, render: (time) => time ? new Date(time).toLocaleString() : '-' },
    { 
      title: '操作', 
      width: 200, 
      fixed: 'right', 
      render: (_, record) => (
        <Space size={4}>
          <Tooltip title="查看详情"><Button type="link" size="small" icon={<FileTextOutlined />} onClick={() => handleViewDetail(record)}>详情</Button></Tooltip>
          {record.status === 'pending' && (
            <>
              <Popconfirm title="确定批准此提现申请吗？" onConfirm={() => handleProcessWithdrawal(record.id, 'approve')}><Button type="link" size="small" style={{ color: '#52c41a' }}>批准</Button></Popconfirm>
              <Popconfirm title="确定拒绝此提现申请吗？" onConfirm={() => handleProcessWithdrawal(record.id, 'reject', '管理员拒绝')}><Button type="link" size="small" danger>拒绝</Button></Popconfirm>
            </>
          )}
        </Space>
      ) 
    },
  ];

  const pendingWithdrawals = withdrawals.filter(w => w.status === 'pending').length;

  return (
    <div className="fit-screen-page">
      {/* 页面标题 */}
      <div className="page-header">
        <h2 className="page-title">商户管理</h2>
      </div>

      {/* 表格容器 */}
      <div className="table-container">
        <Tabs
          defaultActiveKey="affiliates"
          items={[
            {
              key: 'affiliates',
              label: `代理商列表 (${pagination.total})`,
              children: (
                <>
                  {/* 搜索栏 */}
                  <div className="search-bar" style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                    <Input placeholder="搜索推广码" value={searchCode} onChange={(e) => setSearchCode(e.target.value)} style={{ width: 150 }} allowClear />
                    <Input placeholder="搜索用户邮箱" value={searchEmail} onChange={(e) => setSearchEmail(e.target.value)} style={{ width: 200 }} allowClear />
                    <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>搜索</Button>
                    <Button icon={<ReloadOutlined />} onClick={handleReset}>重置</Button>
                  </div>
                  <Table
                    columns={affiliateColumns}
                    dataSource={affiliates}
                    rowKey="id"
                    loading={loading}
                    scroll={{ x: 'max-content', y: 400 }}
                    pagination={{ ...pagination, showSizeChanger: true, showTotal: (total) => `共 ${total} 条记录` }}
                    onChange={handleTableChange}
                    size="small"
                  />
                </>
              ),
            },
            {
              key: 'withdrawals',
              label: `提现申请${pendingWithdrawals > 0 ? ` (${pendingWithdrawals})` : ''}`,
              children: (
                <>
                  <div className="search-bar" style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                    <Button onClick={() => fetchWithdrawals('')}>全部</Button>
                    <Button onClick={() => fetchWithdrawals('pending')}>待处理</Button>
                    <Button onClick={() => fetchWithdrawals('approved')}>已批准</Button>
                    <Button onClick={() => fetchWithdrawals('rejected')}>已拒绝</Button>
                  </div>
                  <Table
                    columns={withdrawalColumns}
                    dataSource={withdrawals}
                    rowKey="id"
                    scroll={{ x: 1000, y: 400 }}
                    pagination={{ pageSize: 10, showSizeChanger: true, showTotal: (total) => `共 ${total} 条` }}
                    size="small"
                  />
                </>
              ),
            },
          ]}
        />
      </div>

      <Modal title="设置代理商" open={affiliateModalVisible} onCancel={() => setAffiliateModalVisible(false)} onOk={() => affiliateForm.submit()}>
        <Form form={affiliateForm} layout="vertical" onFinish={handleAffiliateSubmit}>
          <Form.Item label="用户邮箱"><Input value={selectedAffiliate?.user?.email} disabled /></Form.Item>
          <Form.Item name="commission_rate" label="佣金比例"><InputNumber min={0} max={1} step={0.01} style={{ width: '100%' }} formatter={value => `${(value * 100).toFixed(0)}%`} parser={value => parseFloat(value.replace('%', '')) / 100} /></Form.Item>
        </Form>
      </Modal>

      {/* 提现详情弹窗 */}
      <Modal 
        title="提现申请详情" 
        open={detailModalVisible} 
        onCancel={() => setDetailModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setDetailModalVisible(false)}>关闭</Button>,
          selectedWithdrawal?.status === 'pending' && (
            <>
              <Popconfirm key="reject" title="确定拒绝此提现申请吗？" onConfirm={() => { handleProcessWithdrawal(selectedWithdrawal.id, 'reject', '管理员拒绝'); setDetailModalVisible(false); }}>
                <Button key="reject" danger>拒绝</Button>
              </Popconfirm>
              <Popconfirm key="approve" title="确定批准此提现申请吗？" onConfirm={() => { handleProcessWithdrawal(selectedWithdrawal.id, 'approve'); setDetailModalVisible(false); }}>
                <Button type="primary" key="approve">批准</Button>
              </Popconfirm>
            </>
          )
        ]}
        width={500}
      >
        {selectedWithdrawal && (
          <div style={{ lineHeight: 2 }}>
            <p><strong>代理商信息</strong></p>
            <p style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px dashed #eee', paddingBottom: 8 }}>
              <span>代理商邮箱：</span>
              <Space>
                <span>{selectedWithdrawal.affiliate_email || '-'}</span>
                {selectedWithdrawal.affiliate_email && <CopyOutlined style={{ cursor: 'pointer', color: '#1890ff' }} onClick={() => handleCopy(selectedWithdrawal.affiliate_email, '代理商邮箱')} />}
              </Space>
            </p>
            <p style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px dashed #eee', paddingBottom: 8 }}>
              <span>推广码：</span>
              <Space>
                <span>{selectedWithdrawal.affiliate_code || '-'}</span>
                {selectedWithdrawal.affiliate_code && <CopyOutlined style={{ cursor: 'pointer', color: '#1890ff' }} onClick={() => handleCopy(selectedWithdrawal.affiliate_code, '推广码')} />}
              </Space>
            </p>
            
            <p style={{ marginTop: 16 }}><strong>提现信息</strong></p>
            <p style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px dashed #eee', paddingBottom: 8 }}>
              <span>提现金额：</span>
              <span style={{ color: '#52c41a', fontWeight: 'bold' }}>¥{parseFloat(selectedWithdrawal.amount).toFixed(2)}</span>
            </p>
            <p style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px dashed #eee', paddingBottom: 8 }}>
              <span>申请时间：</span>
              <span>{selectedWithdrawal.created_at ? new Date(selectedWithdrawal.created_at).toLocaleString() : '-'}</span>
            </p>
            <p style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px dashed #eee', paddingBottom: 8 }}>
              <span>当前状态：</span>
              <Tag color={{ pending: 'orange', approved: 'blue', rejected: 'red', paid: 'green' }[selectedWithdrawal.status]}>
                {{ pending: '待处理', approved: '已批准', rejected: '已拒绝', paid: '已支付' }[selectedWithdrawal.status]}
              </Tag>
            </p>
            
            <p style={{ marginTop: 16 }}><strong>银行信息</strong></p>
            <p style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px dashed #eee', paddingBottom: 8 }}>
              <span>银行名称：</span>
              <Space>
                <span>{selectedWithdrawal.bank_name || '-'}</span>
                {selectedWithdrawal.bank_name && <CopyOutlined style={{ cursor: 'pointer', color: '#1890ff' }} onClick={() => handleCopy(selectedWithdrawal.bank_name, '银行名称')} />}
              </Space>
            </p>
            <p style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px dashed #eee', paddingBottom: 8 }}>
              <span>支行名称：</span>
              <Space>
                <span>{selectedWithdrawal.bank_branch || '-'}</span>
                {selectedWithdrawal.bank_branch && <CopyOutlined style={{ cursor: 'pointer', color: '#1890ff' }} onClick={() => handleCopy(selectedWithdrawal.bank_branch, '支行名称')} />}
              </Space>
            </p>
            <p style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px dashed #eee', paddingBottom: 8 }}>
              <span>持卡人姓名：</span>
              <Space>
                <span>{selectedWithdrawal.account_name || '-'}</span>
                {selectedWithdrawal.account_name && <CopyOutlined style={{ cursor: 'pointer', color: '#1890ff' }} onClick={() => handleCopy(selectedWithdrawal.account_name, '持卡人姓名')} />}
              </Space>
            </p>
            <p style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px dashed #eee', paddingBottom: 8 }}>
              <span>银行卡号：</span>
              <Space>
                <span>{selectedWithdrawal.account_number || '-'}</span>
                {selectedWithdrawal.account_number && <CopyOutlined style={{ cursor: 'pointer', color: '#1890ff' }} onClick={() => handleCopy(selectedWithdrawal.account_number, '银行卡号')} />}
              </Space>
            </p>
            {selectedWithdrawal.bank_remark && (
              <p style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px dashed #eee', paddingBottom: 8 }}>
                <span>备注：</span>
                <span>{selectedWithdrawal.bank_remark}</span>
              </p>
            )}
            
            {selectedWithdrawal.processed_at && (
              <>
                <p style={{ marginTop: 16 }}><strong>处理信息</strong></p>
                <p style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px dashed #eee', paddingBottom: 8 }}>
                  <span>处理时间：</span>
                  <span>{new Date(selectedWithdrawal.processed_at).toLocaleString()}</span>
                </p>
              </>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default Affiliates;
